import { VALUE_TYPE } from "Components/NumberValue";
import { formatToLocaleString } from "Formatting/formatNumber";
import { getPriceFormatFromValue } from "Formatting/index";
import { UserAuthorizationLevel } from "Store/advice/interfaces";
import { AdviceType } from "Store/advicePDF/interfaces";
import {
  CollectiveAdvice,
  CoverageNeed,
  NeedName,
  ProductCoverageName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  getCoveragePayment,
  getPayment,
  getSelectedCoverageValue,
  isCoverageSelected,
  isDisplayedAndChecked,
  isDisplayedAndNotChecked,
  isNotDisplayed,
} from "Store/akaAdvice/interfaces/getters";
import { getCorrectAkaAdviceValues } from "Store/akaAdvice/mappers/mapToFormValues/utils";
import { isDefined, isEmpty } from "Utils/functions";
import { AkaAdvicePriceFormValues } from "../AkaAdviceCards/interfaces";
import { SortedNeed, checkShouldPurchaseFromAdvisor } from "../utils";
import { getUnavailableReason } from "./adviceIntroMessagesUtils";
import { getInlineDisplayName } from "./adviceMessagesUtils";

export enum AdviceDiffType {
  NO_DIFF,
  CHECKED_DIFF,
  SLIDER_DIFF,
  INFO,
  UNAVAILABLE,
}

export interface DiffMessageValues {
  productCoverageName: ProductCoverageName;
  diffType: AdviceDiffType;
  message: string;
  additionalMessages: string[];
}

export const generateDiffMessage = (
  isAdvisor: boolean,
  need: SortedNeed,
  formValues: AkaAdvicePriceFormValues,
  adviceMessagesText: any,
  lang: string,
): DiffMessageValues | undefined => {
  const { coverageNeed, name } = need;
  const productCoverageName = mapToProductCoverageName(name);

  if (isNotDisplayed(coverageNeed, productCoverageName)) {
    return handleNotDisplayed(
      isAdvisor,
      coverageNeed,
      productCoverageName,
      adviceMessagesText,
      lang,
    );
  }

  if (isDisplayedAndChecked(coverageNeed, productCoverageName)) {
    return handleDisplayedAndChecked(
      isAdvisor,
      coverageNeed,
      productCoverageName,
      formValues,
      adviceMessagesText,
      lang,
    );
  }

  if (isDisplayedAndNotChecked(coverageNeed, productCoverageName)) {
    return handleDisplayedAndNotChecked(
      isAdvisor,
      coverageNeed,
      productCoverageName,
      formValues,
      adviceMessagesText,
      lang,
    );
  }

  return undefined;
};

const handleDisplayedAndChecked = (
  isAdvisor: boolean,
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
  formValues: AkaAdvicePriceFormValues,
  adviceMessagesText: any,
  lang: string,
): DiffMessageValues => {
  const shouldPurchaseFromAdvisor = checkShouldPurchaseFromAdvisor(
    isAdvisor,
    need,
    productCoverageName,
  );

  if (shouldPurchaseFromAdvisor) {
    return handlePurchaseFromAdvisor(
      isAdvisor,
      need,
      productCoverageName,
      formValues,
      adviceMessagesText,
      lang,
    );
  }

  return handlePurchaseAsCustomer(
    isAdvisor,
    need,
    productCoverageName,
    formValues,
    adviceMessagesText,
    lang,
  );
};

const handlePurchaseFromAdvisor = (
  isAdvisor: boolean,
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
  formValues: AkaAdvicePriceFormValues,
  adviceMessagesText: any,
  lang: string,
): DiffMessageValues => {
  const { checked, sliderValue } = getFormValuesForProduct(
    productCoverageName,
    formValues,
  );
  const sliderValueString = getValueAsString(sliderValue, productCoverageName);

  const { adviceValue } = getCorrectAkaAdviceValues(
    need,
    productCoverageName,
    false,
  );
  const adviceValueString = getValueAsString(adviceValue, productCoverageName);

  const { adviceValue: advisorAdviceValue } = getCorrectAkaAdviceValues(
    need,
    productCoverageName,
    true,
  );
  const advisorAdviceValueString = getValueAsString(
    advisorAdviceValue,
    productCoverageName,
  );

  const nameAsString = getInlineDisplayName(productCoverageName);

  const additionalMessages: string[] =
    productCoverageName === ProductCoverageName.DISABILITY_FLEX_PENSION
      ? handleAdditionalMessagesForDisability(
          isAdvisor,
          need,
          adviceMessagesText.adviceUnavailableMessages.purchaseFromAdvisor,
          lang,
        )
      : [
          `Vi anbefaler en engangsutbetaling på ${advisorAdviceValueString}. Ring oss på 915 08 880 mellom 08:00 og 16:00 på hverdager, så hjelper vi deg.`,
        ];

  if (!checked) {
    return {
      productCoverageName,
      diffType: AdviceDiffType.CHECKED_DIFF,
      message: `Vi anbefaler at du også velger ${nameAsString}${getAdditionalReason(
        productCoverageName,
      )}.`,
      additionalMessages,
    };
  }

  const optionalPaymentText = getOptionalPaymentText(
    isAdvisor,
    productCoverageName,
    formValues,
    adviceMessagesText.adviceUnavailableMessages.purchaseFromAdvisor,
    lang,
  );

  if (sliderValue !== adviceValue) {
    return {
      productCoverageName,
      diffType: AdviceDiffType.SLIDER_DIFF,
      message: `Du har valgt ${nameAsString} med dekning på ${sliderValueString}. Vi anbefalte ${adviceValueString} som er det høyeste beløpet vi kan tilby deg ved kjøp på nett.${optionalPaymentText}`,
      additionalMessages,
    };
  }

  return {
    productCoverageName,
    diffType: AdviceDiffType.SLIDER_DIFF,
    message: `Du har valgt ${nameAsString} med dekning på ${sliderValueString} som er det høyeste beløpet vi kan tilby deg ved kjøp på nett.${optionalPaymentText}`,
    additionalMessages,
  };
};

const handleAdditionalMessagesForDisability = (
  isAdvisor: boolean,
  need: CoverageNeed,
  purchaseFromAdvisorText: any,
  lang: string,
): string[] => {
  const purchaseFromAdvisorMessage = getDisabilityPurchaseFromAdvisorMessage(
    isAdvisor,
    need,
    purchaseFromAdvisorText,
    lang,
  );

  if (!isDefined(purchaseFromAdvisorMessage)) {
    return [];
  }

  return [
    `Vi anbefaler en ${purchaseFromAdvisorMessage}. Ring oss på 915 08 880 mellom 08:00 og 16:00 på hverdager, så hjelper vi deg.`,
  ];
};

const getDisabilityPurchaseFromAdvisorMessage = (
  isAdvisor: boolean,
  need: CoverageNeed,
  purchaseFromAdvisorText: any,
  lang: string,
): string | undefined => {
  const shouldPurchasePensionFromAdvisor = checkShouldPurchaseFromAdvisor(
    isAdvisor,
    need,
    ProductCoverageName.DISABILITY_FLEX_PENSION,
  );
  const shouldPurchaseCapitalFromAdvisor = checkShouldPurchaseFromAdvisor(
    isAdvisor,
    need,
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
  );

  const pensionPayment = getPayment(
    need,
    ProductCoverageName.DISABILITY_FLEX_PENSION,
  );
  const capitalPayment = getPayment(
    need,
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
  );

  const formattedPensionPayment = getPriceFormatFromValue(
    pensionPayment.advice.recommended,
    VALUE_TYPE.NOK,
  );
  const formattedCapitalPayment = getPriceFormatFromValue(
    capitalPayment.advice.recommended,
    VALUE_TYPE.NOK,
  );
  const { oneTime, yearly, andOneTime } = purchaseFromAdvisorText.payment;
  if (shouldPurchasePensionFromAdvisor && shouldPurchaseCapitalFromAdvisor) {
    return `${yearly[lang].replace(
      "{{value}}",
      formattedPensionPayment,
    )} ${andOneTime[lang].replace("{{value}}", formattedCapitalPayment)}`;
  } else if (
    shouldPurchasePensionFromAdvisor &&
    !shouldPurchaseCapitalFromAdvisor
  ) {
    return `${yearly[lang].replace("{{value}}", formattedPensionPayment)}`;
  } else if (
    !shouldPurchasePensionFromAdvisor &&
    shouldPurchaseCapitalFromAdvisor
  ) {
    return `${oneTime[lang].replace("{{value}}", formattedCapitalPayment)}`;
  }

  return undefined;
};

const handlePurchaseAsCustomer = (
  isAdvisor: boolean,
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
  formValues: AkaAdvicePriceFormValues,
  adviceMessagesText: any,
  lang: string,
): DiffMessageValues => {
  const { checked, sliderValue } = getFormValuesForProduct(
    productCoverageName,
    formValues,
  );
  const sliderValueString = getValueAsString(sliderValue, productCoverageName);

  const { adviceValue, minAdviceValue, maxAdviceValue } =
    getCorrectAkaAdviceValues(need, productCoverageName, false);
  const adviceValueString = getValueAsString(adviceValue, productCoverageName);

  const nameAsString = getInlineDisplayName(productCoverageName);

  if (!checked) {
    return {
      productCoverageName,
      diffType: AdviceDiffType.CHECKED_DIFF,
      message: `Vi anbefaler at du også velger ${nameAsString}${getAdditionalReason(
        productCoverageName,
      )}.`,
      additionalMessages: [],
    };
  }

  const optionalPaymentText = getOptionalPaymentText(
    isAdvisor,
    productCoverageName,
    formValues,
    adviceMessagesText.adviceUnavailableMessages.purchaseFromAdvisor,
    lang,
  );

  const additionalMessages =
    productCoverageName === ProductCoverageName.DISABILITY_FLEX_PENSION
      ? handleAdditionalMessagesForDisability(
          isAdvisor,
          need,
          adviceMessagesText.adviceUnavailableMessages.purchaseFromAdvisor,
          lang,
        )
      : [];

  if (sliderValue !== adviceValue) {
    const isWithinLeeway =
      sliderValue >= minAdviceValue && sliderValue <= maxAdviceValue;
    return {
      productCoverageName,
      diffType: isWithinLeeway
        ? AdviceDiffType.NO_DIFF
        : AdviceDiffType.SLIDER_DIFF,
      message: `Du har valgt ${nameAsString} med dekning på ${sliderValueString}. Vi anbefalte ${adviceValueString}.${optionalPaymentText}`,
      additionalMessages,
    };
  }

  return {
    productCoverageName,
    diffType: isEmpty(additionalMessages)
      ? AdviceDiffType.NO_DIFF
      : AdviceDiffType.SLIDER_DIFF,
    message: `Du har valgt å følge vår anbefaling for ${nameAsString} med dekning på ${sliderValueString}.${optionalPaymentText}`,
    additionalMessages,
  };
};

const getOptionalPaymentText = (
  isAdvisor: boolean,
  productCoverageName: ProductCoverageName,
  formValues: AkaAdvicePriceFormValues,
  purchaseFromAdvisorText: any,
  lang: string,
): string => {
  if (
    productCoverageName !== ProductCoverageName.DISABILITY_FLEX_PENSION ||
    !isCoverageSelected(
      ProductCoverageName.DISABILITY_FLEX_CAPITAL,
      formValues,
      isAdvisor,
    )
  ) {
    return "";
  }

  return purchaseFromAdvisorText.optionalOneTimePayment[lang].replace(
    "{{value}}",
    `${formatToLocaleString(
      getSelectedCoverageValue(
        ProductCoverageName.DISABILITY_FLEX_CAPITAL,
        formValues,
        isAdvisor,
      ),
    )} ${VALUE_TYPE.NOK}`,
  );
};

const handleDisplayedAndNotChecked = (
  isAdvisor: boolean,
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
  formValues: AkaAdvicePriceFormValues,
  adviceMessagesText: any,
  lang: string,
): DiffMessageValues | undefined => {
  const { checked, sliderValue } = getFormValuesForProduct(
    productCoverageName,
    formValues,
  );
  const sliderValueString = getValueAsString(sliderValue, productCoverageName);

  const nameAsString = getInlineDisplayName(productCoverageName);

  if (checked) {
    return {
      productCoverageName,
      diffType: AdviceDiffType.SLIDER_DIFF,
      message: `Du har valgt ${nameAsString} med dekning på ${sliderValueString}. ${
        handleNotDisplayed(
          isAdvisor,
          need,
          productCoverageName,
          adviceMessagesText,
          lang,
        )?.message ?? ""
      }`,
      additionalMessages: [],
    };
  }

  return handleNotDisplayed(
    isAdvisor,
    need,
    productCoverageName,
    adviceMessagesText,
    lang,
  );
};

const handleNotDisplayed = (
  isAdvisor: boolean,
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
  adviceMessagesText: any,
  lang: string,
): DiffMessageValues | undefined => {
  const unavailableReason = getUnavailableReason(
    isAdvisor,
    need,
    productCoverageName,
    adviceMessagesText.adviceUnavailableMessages,
    lang,
  );

  if (isDefined(unavailableReason)) {
    return {
      productCoverageName,
      diffType: AdviceDiffType.UNAVAILABLE,
      message: unavailableReason,
      additionalMessages: [],
    };
  }

  return undefined;
};

const mapToProductCoverageName = (needName: NeedName): ProductCoverageName => {
  switch (needName) {
    case NeedName.LIFE:
      return ProductCoverageName.DEATH;
    case NeedName.DISABILITY:
      return ProductCoverageName.DISABILITY_FLEX_PENSION;
    case NeedName.CRITICAL_ILLNESS:
      return ProductCoverageName.CRITICAL_ILLNESS;
  }
};

const getFormValuesForProduct = (
  productCoverageName: ProductCoverageName,
  formValues: AkaAdvicePriceFormValues,
): { checked: boolean; sliderValue: number } => {
  switch (productCoverageName) {
    case ProductCoverageName.DEATH:
      return {
        checked: formValues.deathCheckbox,
        sliderValue: formValues.deathInsuredSumSlider ?? 0,
      };
    case ProductCoverageName.DISABILITY_FLEX_PENSION:
      return {
        checked: formValues.disabilityCheckbox,
        sliderValue: formValues.disabilityInsuredSumSlider ?? 0,
      };
    case ProductCoverageName.CRITICAL_ILLNESS:
      return {
        checked: formValues.criticalIllnessCheckbox,
        sliderValue: formValues.criticalIllnessInsuredSumSlider ?? 0,
      };
    default:
      return {
        checked: false,
        sliderValue: 0,
      };
  }
};

const getValueAsString = (
  value: number,
  productCoverageName: ProductCoverageName,
): string => {
  return getPriceFormatFromValue(
    value,
    productCoverageName === ProductCoverageName.DISABILITY_FLEX_PENSION
      ? VALUE_TYPE.NOK_YEAR
      : VALUE_TYPE.NOK,
  );
};

const getAdditionalReason = (
  productCoverageName: ProductCoverageName,
): string => {
  if (productCoverageName === ProductCoverageName.CRITICAL_ILLNESS) {
    return " som kan være til god hjelp hvis du skulle få alvorlig sykdom";
  }
  return "";
};

export const mustVerifyAdviceChoices = (
  advice: CollectiveAdvice,
  formValues: Partial<AkaAdvicePriceFormValues>,
  adviceType: AdviceType,
  isAdvisor: boolean,
): boolean =>
  !isAdvisor &&
  adviceType === AdviceType.INDIVIDUAL &&
  Object.values(ProductCoverageName).some((productCoverageName) =>
    mustVerifyCoverageChoice(
      advice,
      productCoverageName,
      formValues,
      isAdvisor,
    ),
  );

const mustVerifyCoverageChoice = (
  advice: CollectiveAdvice,
  insuranceProduct: ProductCoverageName,
  formValues: Partial<AkaAdvicePriceFormValues>,
  isAdvisor: boolean,
): boolean => {
  const paymentConfiguration = getCoveragePayment(advice, insuranceProduct);

  if (!isDefined(paymentConfiguration)) {
    return false;
  }

  const maxPayoutCustomer =
    paymentConfiguration.restrictions.paymentRestriction.maxPayouts[
      UserAuthorizationLevel.CUSTOMER
    ];

  if (paymentConfiguration.advice.recommended <= maxPayoutCustomer) {
    return false;
  }

  const coverageIsSelected = isCoverageSelected(
    insuranceProduct,
    formValues,
    isAdvisor,
  );
  if (insuranceProduct === ProductCoverageName.DISABILITY_FLEX_CAPITAL) {
    return !coverageIsSelected;
  }

  const selectedCoverageValue = getSelectedCoverageValue(
    insuranceProduct,
    formValues,
    isAdvisor,
  );
  return coverageIsSelected && selectedCoverageValue === maxPayoutCustomer;
};
